import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, HeartPulse, Phone, UserRound, Target, Activity, Zap, MessageSquare, type LucideIcon } from 'lucide-react';
import { parseClientQuestionnaires, questionnaireBoolean, type QuestionnaireField, type QuestionnaireSection } from '@/lib/trials/clientQuestionnaire';

export interface QuestionnaireIdentity {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}
interface Props { notes?: string | null; identity?: QuestionnaireIdentity }
const groups: { section: QuestionnaireSection; icon: LucideIcon; fields: QuestionnaireField[] }[] = [
  { section: 'personal', icon: UserRound, fields: ['age', 'gender', 'height', 'weight', 'clothingSize'] },
  { section: 'emergency', icon: Phone, fields: ['emergencyPhone'] },
  { section: 'goal', icon: Target, fields: ['goal'] },
  { section: 'training', icon: Activity, fields: ['currentlyTraining', 'trainingType', 'trainingFrequency', 'lastTrained', 'availability'] },
  { section: 'ems', icon: Zap, fields: ['triedEms', 'emsFeedback'] },
  { section: 'motivation', icon: MessageSquare, fields: ['motivation'] },
];
const goals = new Set(['fat_loss', 'fitness', 'performance', 'toning', 'posture', 'rehab', 'wellness', 'muscle', 'return', 'other']);

export function ClientQuestionnaireSummary({ notes, identity = {} }: Props) {
  const { t } = useTranslation('clients', { keyPrefix: 'questionnaire' });
  const summary = useMemo(() => parseClientQuestionnaires(notes), [notes]);
  const displayValue = (key: QuestionnaireField, value: string | undefined) => {
    if (!value) return t('missing');
    if (key === 'height') return `${value} cm`;
    if (key === 'weight') return `${value} kg`;
    if (key === 'age') return t('years', { value });
    if (key === 'gender') return t(value, { defaultValue: value });
    if (key === 'goal') {
      if (goals.has(value)) return t(value);
      if (value.startsWith('Other:')) return `${t('other')} : ${value.slice(6).trim()}`;
    }
    if (key === 'currentlyTraining' || key === 'triedEms') {
      const answer = questionnaireBoolean(value);
      return answer === null ? value : t(answer ? 'yes' : 'no');
    }
    if (key === 'limitations') {
      return value.replace(/^Yes\b/, t('yes')).replace(/^No\b/, t('no'));
    }
    return value;
  };
  const field = (key: string, value: string | undefined, phone = false) => (
    <div key={key} className="min-w-0">
      <dt className="text-xs font-medium text-muted-foreground">{t(key)}</dt>
      <dd className={`mt-1 whitespace-pre-wrap break-words text-sm leading-6 ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
        {phone && value && /^[+\d\s().-]+$/.test(value) ? <a className="text-primary underline-offset-4 hover:underline" href={`tel:${value.replace(/[^+\d]/g, '')}`}>{value}</a> : value || t('missing')}
      </dd>
    </div>
  );
  return (
    <section aria-label={t('title')} className="min-w-0 border-b border-border pb-6">
      <header className="mb-5 flex items-center gap-3">
        <ClipboardList className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <h2 className="text-lg font-semibold text-foreground">{t('title')}</h2>
      </header>
      {!summary.questionnaires.length ? <p className="text-sm text-muted-foreground">{t('empty')}</p> :
        summary.questionnaires.map((questionnaire, index) => (
          <article key={index} className="min-w-0 space-y-6 border-t border-border py-5 first:border-t-0 first:pt-0">
            {questionnaire.date && <p className="text-sm font-medium text-muted-foreground">{t('date', { date: questionnaire.date })}</p>}
            <div className="grid min-w-0 grid-cols-1 gap-x-8 gap-y-6 lg:grid-cols-2">
              {groups.map(({ section, icon: Icon, fields }) => (
                <section key={section} className="min-w-0">
                  <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground"><Icon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />{t(section)}</h3>
                  <dl className={`grid min-w-0 gap-3 ${section === 'personal' ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {section === 'personal' && Object.entries({ firstName: identity.firstName, lastName: identity.lastName, phone: identity.phone, email: identity.email }).map(([key, value]) => field(key, value, key === 'phone'))}
                    {fields.map(key => field(key, questionnaire.fields[key] ? displayValue(key, questionnaire.fields[key]) : undefined, key === 'emergencyPhone'))}
                  </dl>
                </section>
              ))}
            </div>
            <section className="min-w-0">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold"><HeartPulse className="h-4 w-4 text-primary" aria-hidden="true" />{t('medical')}</h3>
              {questionnaire.medical.length ? <div className="divide-y divide-border">
                {(['general', 'cardiovascular', 'injuries', 'treatment'] as const).map(topic => {
                  const response = questionnaire.medical.find(item => item.topic === topic);
                  const answer = response?.answer;
                  return <div key={topic} className="grid min-w-0 gap-2 py-3 sm:grid-cols-[1fr_auto]">
                    <div className="min-w-0"><h4 className="text-sm font-medium">{t(topic)}</h4>{response?.details && <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">{response.details}</p>}</div>
                    <span className={`w-fit self-start rounded-md border px-2 py-1 text-xs font-medium ${answer === true ? 'border-status-warning-border bg-status-warning text-status-warning-foreground' : 'border-border bg-muted text-muted-foreground'}`}>{t(answer === true ? 'yes' : answer === false ? 'no' : 'missing')}</span>
                  </div>;
                })}
              </div> : <dl>{field('limitations', questionnaire.fields.limitations ? displayValue('limitations', questionnaire.fields.limitations) : undefined)}</dl>}
            </section>
            {questionnaire.additional && <section><h3 className="mb-2 text-sm font-semibold">{t('additional')}</h3><p className="whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">{questionnaire.additional}</p></section>}
          </article>
        ))}
      {summary.additional && summary.questionnaires.length > 0 && <section className="mt-5 border-t border-border pt-5"><h3 className="mb-2 text-sm font-semibold">{t('additional')}</h3><p className="whitespace-pre-wrap break-words text-sm leading-6 text-muted-foreground">{summary.additional}</p></section>}
    </section>
  );
}